'use client';

import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Synchronous, non-hook check for `prefers-reduced-motion`. Use this instead of the hook in
 * code that can't call hooks (event handlers, plain utility functions) — the hook itself uses
 * this under the hood so both stay in sync on the same media query string.
 *
 * @returns `true` if the user currently prefers reduced motion, `false` otherwise (including
 * during SSR, where there is no `window` to read the preference from).
 *
 * @example
 * ```ts
 * export function withViewTransition(callback: () => void): void {
 *   if (getPrefersReducedMotion()) return callback();
 *   document.startViewTransition(callback);
 * }
 * ```
 */
export function getPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * Hook that detects if the user has requested reduced motion in their system preferences.
 *
 * This hook respects the user's `prefers-reduced-motion` media query setting, which is an
 * accessibility preference that indicates the user wants to minimize non-essential motion
 * and animations. Users with vestibular disorders, motion sensitivity, or other conditions
 * may experience discomfort, dizziness, or nausea from excessive animations.
 *
 * **Note**: For most use cases, prefer Tailwind's `motion-safe:` and `motion-reduce:` utility classes,
 * which automatically handle reduced motion preferences without JavaScript. Use this hook primarily
 * when working with third-party libraries or when you need programmatic access to the preference.
 *
 * @example
 * ```tsx
 * // Preferred approach with Tailwind utilities
 * <div className="motion-safe:animate-spin motion-reduce:animate-none">
 *   Content
 * </div>
 *
 * // Use this hook for third-party libraries
 * function AnimatedComponent() {
 *   const prefersReducedMotion = usePrefersReducedMotion();
 *
 *   return (
 *     <ThirdPartyCarousel
 *       autoplay={!prefersReducedMotion}
 *       transition={prefersReducedMotion ? 'none' : 'slide'}
 *     />
 *   );
 * }
 * ```
 *
 * @returns {boolean} `true` if the user prefers reduced motion, `false` otherwise
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(getPrefersReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);

    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);

    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  return prefersReducedMotion;
}
